import { counterProcessSessionEventsService } from '@/api/process-session-events.service';
import { counterProcessSessionService } from '@/api/counter-process-session.service';
import type { ProcessSessionEventDto, ProcessSessionResponseDto } from '@carvia/ros-client-types';
import type { ServerSentEventService } from '@/api/server-sent-event.service';
import { isNewerProcessSessionEvent } from '@/queries/use-process-session';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, onUnmounted, ref, toValue, watch, type MaybeRefOrGetter } from 'vue';

export function useCounterProcessSession(counterId: MaybeRefOrGetter<string | undefined>) {
  return useQuery({
    queryKey: ['counter-process-session', toValue(counterId)],
    queryFn: () => counterProcessSessionService.getProcessSession(toValue(counterId)!),
    enabled: computed(() => !!toValue(counterId)),
  });
}

export function useCounterContractTemplate(counterId: MaybeRefOrGetter<string | undefined>) {
  return useQuery({
    queryKey: ['counter-process-session-contract', toValue(counterId)],
    queryFn: () => counterProcessSessionService.getContractHtml(toValue(counterId)!),
    enabled: computed(() => !!toValue(counterId)),
  });
}

export function useCounterProcessSessionEvents(
  counterId: MaybeRefOrGetter<string | undefined>,
  enabled: MaybeRefOrGetter<boolean> = true,
) {
  const queryClient = useQueryClient();
  const isReconnecting = ref(false);

  let subscription: { unsubscribe: () => void } | null = null;
  let service: ServerSentEventService<ProcessSessionEventDto> | null = null;
  let wasConnected = false;

  const stop = () => {
    subscription?.unsubscribe();
    subscription = null;
    service?.close();
    service = null;
    wasConnected = false;
    isReconnecting.value = false;
  };

  watch(
    [() => toValue(counterId), () => toValue(enabled)],
    ([id, isEnabled]) => {
      stop();
      if (!isEnabled || !id) return;

      service = counterProcessSessionEventsService(id);
      subscription = service
        .open({
          onConnectionStateChange(state) {
            isReconnecting.value = state === 'reconnecting';
            if (state === 'connected') {
              if (wasConnected) {
                void queryClient.refetchQueries({ queryKey: ['counter-process-session', id] });
              }
              wasConnected = true;
            }
          },
        })
        .subscribe({
          next: (event) => {
            const cached = queryClient.getQueryData<ProcessSessionResponseDto | null | undefined>([
              'counter-process-session',
              id,
            ]);
            if (!isNewerProcessSessionEvent(event, cached, ['session.ended', 'session.detached'])) {
              return;
            }
            void queryClient.refetchQueries({ queryKey: ['counter-process-session', id] });
          },
        });
    },
    { immediate: true },
  );

  onUnmounted(stop);

  return {
    isReconnecting: computed(() => isReconnecting.value),
  };
}
